import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { getMe, login } from '../lib/api.js';
import { SessionContext } from '../hooks/index.js';
import BrandMark from './ui/BrandMark.jsx';
import Icon from './ui/Icon.jsx';
import WorkspaceStatus from './ui/WorkspaceStatus.jsx';

/**
 * Wraps the app in a sign-in screen. While the session is being checked it
 * shows a branded loading state without exposing the protected workspace.
 */
export default function AuthGate({ children }) {
  const [state, setState] = useState({ checking: true, required: true, username: null, admin: false, error: null });
  const request = useRef(null);

  const refresh = useCallback(() => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setState((previous) => ({ ...previous, checking: true, error: null }));
    getMe(controller.signal)
      .then((r) =>
        !controller.signal.aborted && setState({
          checking: false,
          required: r.authRequired !== false,
          username: r.username ?? null,
          admin: r.admin === true,
          error: null
        })
      )
      .catch((err) => {
        if (!controller.signal.aborted) setState((previous) => ({ ...previous, checking: false, error: err.message }));
      });
  }, []);

  useEffect(() => {
    refresh();
    const onExpired = () => setState((s) => ({ ...s, username: null, admin: false }));
    window.addEventListener('auth:expired', onExpired);
    return () => {
      request.current?.abort();
      window.removeEventListener('auth:expired', onExpired);
    };
  }, [refresh]);

  if (state.checking) return <WorkspaceStatus title="Opening your workspace">Checking your sign-in session.</WorkspaceStatus>;
  if (state.error) return <WorkspaceStatus title="We could not connect." error={state.error} onRetry={refresh} />;
  if (!state.required) return children;
  if (!state.username) {
    return <SignIn onDone={(username, admin) => setState((s) => ({ ...s, username, admin }))} />;
  }

  return (
    <SessionContext.Provider value={{ username: state.username, admin: state.admin }}>
      {children}
    </SessionContext.Provider>
  );
}

function SignIn({ onDone }) {
  const passwordId = useId();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    login(username, password)
      .then((r) => onDone(r.username, r.admin === true))
      .catch((err) => {
        setError(err.message);
        setPassword('');
      })
      .finally(() => setBusy(false));
  };

  return (
    <main className="signin">
      <section className="signin-story" aria-label="About Pakka Paisa">
        <div className="workspace-brand"><BrandMark /><span>Pakka Paisa</span></div>
        <div className="signin-story-copy">
          <p className="eyebrow">A considered approach to investing</p>
          <p className="signin-statement">Less noise.<br /><em>More perspective.</em></p>
          <p className="signin-story-description">A calmer space to research Indian markets, understand the trade-offs, and make your own informed decisions.</p>
          <div className="signin-principles">
            <span><Icon name="chart" />Research with context</span>
            <span><Icon name="leaf" />Think beyond today</span>
            <span><Icon name="check" />Keep the risks in view</span>
          </div>
        </div>
        <p className="signin-story-foot">Built for thoughtful decisions. Not investment advice.</p>
      </section>
      <div className="signin-form-side">
      <form className="signin-card" onSubmit={submit} aria-busy={busy}>
        <div className="signin-mobile-brand"><BrandMark /><strong>Pakka Paisa</strong></div>
        <p className="eyebrow">Your investing workspace</p>
        <h1>Welcome back.</h1>
        <p className="signin-intro">A little clarity for your next decision.</p>

        <label className="field">
          <span>Username</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            disabled={busy}
            placeholder="Enter your username"
            required
          />
        </label>

        <div className="field">
          <label className="field-label" htmlFor={passwordId}>Password</label>
          <div className="password-input">
            <input
              id={passwordId}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Enter your password"
              disabled={busy}
              required
            />
            <button type="button" className="icon-btn" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword((value) => !value)}><Icon name="eye" /></button>
          </div>
        </div>

        {error && <p className="signin-error" role="alert">{error}</p>}

        <button className="btn primary" type="submit" disabled={busy || !username || !password}>
          {busy ? 'Signing in…' : 'Enter your workspace'}
          <Icon name={busy ? "refresh" : "arrowRight"} className={busy ? "spin" : ""} />
        </button>
        <p className="signin-access-note">Access is managed by your workspace administrator.</p>
      </form>
      </div>
    </main>
  );
}
